import { AlertCircle, CheckCircle } from "lucide-react";
import type React from "react";
import { useCallback, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import {
  type FileValidationResult,
  validateFile,
} from "@/utils/fileValidation";
import DualFileInput from "./DualFileInput";

type FileSide = 'front' | 'back';

interface FileState {
  file: File | null;
  error: string | null;
  isValidating?: boolean;
}

interface DualFileInputContainerProps {
  onFrontFileChange?: (file: File | null, error: string | null) => void;
  onBackFileChange?: (file: File | null, error: string | null) => void;
  onValidationComplete?: (
    frontValid: boolean,
    backValid: boolean,
    frontFile: File | null,
    backFile: File | null,
  ) => void;
  accept?: string;
  frontDescription?: string;
  backDescription?: string;
  className?: string;
}

// Simple Alert component for error display
const Alert: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div
    className={cn(
      "relative w-full rounded-lg border px-4 py-3 text-sm",
      className,
    )}
  >
    {children}
  </div>
);

const AlertDescription: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className="[&_p]:leading-relaxed">{children}</div>;

const DualFileInputContainer: React.FC<DualFileInputContainerProps> = ({
  onFrontFileChange,
  onBackFileChange,
  onValidationComplete,
  accept = "image/*,.pdf",
  frontDescription = "Upload a clear image of the front side",
  backDescription = "Upload a clear image of the back side",
  className
}) => {
  const [frontFile, setFrontFile] = useState<FileState>({ error: null, file: null });
  const [backFile, setBackFile] = useState<FileState>({ error: null, file: null });
  // Validation is async, so callbacks must read the other side's latest file
  // from refs rather than from the render-time closure.
  const latestFiles = useRef<Record<FileSide, File | null>>({ back: null, front: null });
  const validationRunIds = useRef<Record<FileSide, number>>({ back: 0, front: 0 });

  const setSideState = useCallback((type: FileSide, state: FileState) => {
    latestFiles.current[type] = state.file;
    if (type === 'front') {
      setFrontFile(state);
    } else {
      setBackFile(state);
    }
  }, []);

  const reportValidationComplete = useCallback(() => {
    const { front, back } = latestFiles.current;
    onValidationComplete?.(front !== null, back !== null, front, back);
  }, [onValidationComplete]);

  const handleFileValidation = useCallback(async (file: File): Promise<FileValidationResult> => {
    return await validateFile(file);
  }, []);

  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>, type: FileSide) => {
    const file = event.target.files?.[0] || null;
    const runId = ++validationRunIds.current[type];
    const isStale = () => validationRunIds.current[type] !== runId;
    
    console.log(`🎭 [DualFileInput] File change event for ${type}:`, {
      hasFile: !!file,
      fileName: file?.name,
      fileSize: file?.size,
      fileType: file?.type
    });
    
    if (file) {
      console.log(`⏳ [DualFileInput] Starting validation for ${type} file: ${file.name}`);
      
      setSideState(type, { file: null, error: null, isValidating: true });

      try {
        const validationResult = await handleFileValidation(file);
        if (isStale()) return;
        console.log(`📋 [DualFileInput] Validation complete for ${type}:`, {
          isValid: validationResult.isValid,
          error: validationResult.error,
          originalFile: file.name,
          resultFile: validationResult.file.name,
          sizeChanged: file.size !== validationResult.file.size,
          originalSize: file.size,
          resultSize: validationResult.file.size
        });
        
        const notifySideChange = type === 'front' ? onFrontFileChange : onBackFileChange;

        if (validationResult.isValid) {
          // File is valid - use the potentially resized file from validation result
          setSideState(type, { file: validationResult.file, error: null, isValidating: false });
          notifySideChange?.(validationResult.file, null);
        } else {
          setSideState(type, { file: null, error: validationResult.error, isValidating: false });
          notifySideChange?.(null, validationResult.error);
        }

        reportValidationComplete();

      } catch (error) {
        if (isStale()) return;
        const errorMessage = error instanceof Error ? error.message : 'Validation failed';
        setSideState(type, { file: null, error: errorMessage, isValidating: false });
        (type === 'front' ? onFrontFileChange : onBackFileChange)?.(null, errorMessage);
        reportValidationComplete();
      }
    } else {
      // No file selected
      setSideState(type, { file: null, error: null, isValidating: false });
      (type === 'front' ? onFrontFileChange : onBackFileChange)?.(null, null);
      reportValidationComplete();
    }
  }, [handleFileValidation, onFrontFileChange, onBackFileChange, reportValidationComplete, setSideState]);

  // Wrapper function to handle file changes and determine front vs back based on input ID
  const handleFileChangeWrapper = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const inputId = event.target.id;
    const type = inputId === 'front-card' ? 'front' : 'back';
    handleFileChange(event, type);
  }, [handleFileChange]);

  return (
    <div className="space-y-2">
      {/* Error Messages */}
      {frontFile.error && (
        <Alert className="text-red-800 border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>
              <strong>Front Card Error:</strong> {frontFile.error}
            </AlertDescription>
          </div>
        </Alert>
      )}
      
      {backFile.error && (
        <Alert className="text-red-800 border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>
              <strong>Back Card Error:</strong> {backFile.error}
            </AlertDescription>
          </div>
        </Alert>
      )}

      {/* Enhanced DualFileInput - we need to modify this to handle separate onChange handlers */}
      <div className="space-y-2">
        <DualFileInput
          accept={accept}
          backDescription={backDescription}
          className={className}
          frontDescription={frontDescription}
          onChange={handleFileChangeWrapper}
        />
      </div>

      {/* Success indicators */}
      {frontFile.file && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <span>Front card: {frontFile.file.name} uploaded successfully</span>
        </div>
      )}
      
      {backFile.file && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <span>Back card: {backFile.file.name} uploaded successfully</span>
        </div>
      )}
    </div>
  );
};

export default DualFileInputContainer;
